import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import { useWorkerProgress } from '../hooks/useWorkerProgress'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import WorkerProgressBanner from '../components/WorkerProgressBanner'
import WebAuthModal from '../components/WebAuthModal'
import BrandQuestionsCard from '../components/BrandQuestionsCard'
import {
  FileText,
  Upload,
  RefreshCw,
  Search,
  Eye,
  AlertCircle,
  Globe,
  TrendingUp,
  Sparkles,
  DollarSign,
  Info,
  ExternalLink,
  Brain,
  X,
  Edit,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

interface ContentItem {
  id: string
  type: string
  url: string
  source: string
  sourceId: string
  enrichmentStatus: string
  createdAt: string
  fields?: Array<{
    type: string
    value: any
    source: string
  }>
}

interface ContentStats {
  total: number
  byType: Record<string, number>
  byStatus: Record<string, number>
  recentlyAdded: number
}

interface BrandAccessibilityStatus {
  isAccessible: boolean
  blockType: string | null
  confidence: number | null
  notes: string | null
  lastChecked: string | null
  detectedPlatform?: string | null
  requiresWebAuth?: boolean
  webAuthType?: string | null
  webAuthConfigured?: boolean
}

export default function ContentManager() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [stats, setStats] = useState<ContentStats | null>(null)
  const [contents, setContents] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [importLoading, setImportLoading] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showEnrichModal, setShowEnrichModal] = useState(false)
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null)
  const [accessibilityStatus, setAccessibilityStatus] = useState<BrandAccessibilityStatus | null>(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 50

  // Import form
  const [importMethod, setImportMethod] = useState<'site' | 'page'>('site')
  const [pageUrl, setPageUrl] = useState('')
  const [urlDomainError, setUrlDomainError] = useState('')

  // Enrichment
  const [enrichmentCost, setEnrichmentCost] = useState<any>(null)

  // Content editing
  const [editedTitle, setEditedTitle] = useState('')
  const [editedDescription, setEditedDescription] = useState('')
  const [editedSummary, setEditedSummary] = useState('')
  const [editedBenefits, setEditedBenefits] = useState('')
  const [editedKeyPoints, setEditedKeyPoints] = useState('')
  const [editedImportanceScore, setEditedImportanceScore] = useState(5)
  const [isSavingContent, setIsSavingContent] = useState(false)
  const [keywordTags, setKeywordTags] = useState<string[]>([])
  const [keywordInput, setKeywordInput] = useState('')
  const [deletingAIContent, setDeletingAIContent] = useState(false)

  // Web Authentication
  const [showWebAuthModal, setShowWebAuthModal] = useState(false)

  // Define fetchData before using it in hooks
  const fetchData = async () => {
    try {
      setLoading(true)
      const [statsRes, contentsRes, accessibilityRes] = await Promise.all([
        api.get(`/content/stats/${activeBrand?.id}`),
        api.get('/content', {
          params: {
            brandId: activeBrand?.id,
            limit: 50,
            includeFields: true
          }
        }),
        api.get(`/content-accessibility/status/${activeBrand?.id}`).catch(() => ({ data: null }))
      ])

      setStats(statsRes.data)
      setContents(contentsRes.data.contents || [])
      if (accessibilityRes.data) {
        setAccessibilityStatus(accessibilityRes.data)
      }
    } catch (error) {
      console.error('Failed to fetch content data:', error)
    } finally {
      setLoading(false)
    }
  }

  // ========================================
  // REFACTORED: Use reusable worker progress hooks
  // ========================================
  const importProgress = useWorkerProgress({
    brandId: activeBrand?.id || '',
    statusEndpoint: '/content/import-status',
    pollInterval: 5000,
    pollTimeout: 120000, // 2 minutes
    completionMessage: 'Import complete!',
    onComplete: fetchData
  })

  const enrichmentProgress = useWorkerProgress({
    brandId: activeBrand?.id || '',
    statusEndpoint: '/content/enrich-status',
    pollInterval: 5000,
    pollTimeout: 300000, // 5 minutes
    completionMessage: 'Enrichment complete!',
    onComplete: fetchData
  })

  // Check for existing active jobs on mount
  useEffect(() => {
    if (activeBrand) {
      fetchData()
      importProgress.checkStatus()
      enrichmentProgress.checkStatus()
    }
  }, [activeBrand])

  // Poll stats while import or enrichment is active
  useEffect(() => {
    if (importProgress.state.status !== 'active' && enrichmentProgress.state.status !== 'active') {
      return
    }

    const interval = setInterval(async () => {
      if (!activeBrand?.id) return
      try {
        // Only poll stats during active import/enrichment
        // Content list refreshes on completion via onComplete: fetchData
        const statsRes = await api.get(`/content/stats/${activeBrand.id}`)
        setStats(statsRes.data)
      } catch (error) {
        console.error('Failed to poll content stats:', error)
      }
    }, 10000) // Poll every 10 seconds

    return () => clearInterval(interval)
  }, [importProgress.state.status, enrichmentProgress.state.status, activeBrand?.id])

  const fetchEnrichmentCost = async () => {
    try {
      const response = await api.post('/content/enrich/estimate', {
        brandId: activeBrand?.id
      })
      setEnrichmentCost(response.data)
    } catch (error) {
      console.error('Failed to estimate enrichment cost:', error)
    }
  }

  const normalizeUrl = (url: string): string => {
    // Strip http:// and https:// prefixes
    let normalized = url.replace(/^https?:\/\//, '')
    // Add https:// prefix
    return `https://${normalized}`
  }

  const getDomainFromUrl = (url: string): string => {
    try {
      const normalized = normalizeUrl(url)
      const urlObj = new URL(normalized)
      return urlObj.hostname
    } catch {
      return ''
    }
  }

  const validatePageUrl = (url: string): boolean => {
    if (!url || !activeBrand?.domain) return false

    const enteredDomain = getDomainFromUrl(url)
    const brandDomain = getDomainFromUrl(`https://${activeBrand.domain}`)

    return Boolean(enteredDomain && brandDomain && enteredDomain === brandDomain)
  }

  const handlePageUrlChange = (value: string) => {
    setPageUrl(value)

    if (value && !validatePageUrl(value)) {
      const brandDomain = activeBrand?.domain || ''
      if (brandDomain) {
        setUrlDomainError(`URL must be from domain: ${brandDomain}`)
      } else {
        setUrlDomainError('URL must be from your brand domain')
      }
    } else {
      setUrlDomainError('')
    }
  }

  const handleImport = async () => {
    if (!activeBrand?.id) return

    try {
      setImportLoading(true)

      // Step 1: Run pre-import accessibility check as validation
      const domain = importMethod === 'site'
        ? normalizeUrl(`https://${activeBrand.domain || ''}`)
        : getDomainFromUrl(pageUrl)

      toast.info('Checking site accessibility...', {
        position: 'top-right',
        autoClose: 2000,
      })

      const accessibilityCheck = await api.post(`/content-accessibility/pre-import-check/${activeBrand.id}`, {
        sampleUrls: importMethod === 'site'
          ? [
              domain,
              `${domain}/products`,
              `${domain}/about`
            ]
          : [pageUrl],
        sampleSize: 3
      })

      const { isAccessible, detectedPlatform, webAuthConfigured } = accessibilityCheck.data

      // Step 2: If blocked and platform detected but no auth configured -> prompt for auth
      if (!isAccessible && detectedPlatform && !webAuthConfigured) {
        setImportLoading(false)
        setShowImportModal(false)

        // Refresh accessibility status to show banner
        await fetchData()

        toast.warning(`${detectedPlatform.charAt(0).toUpperCase() + detectedPlatform.slice(1)} store detected! Please configure authentication to import content.`, {
          position: 'top-right',
          autoClose: 5000,
        })

        // Auto-open the WebAuth modal
        setShowWebAuthModal(true)
        return
      }

      // Step 3: If blocked but auth IS configured, or site is accessible -> proceed with import
      if (importMethod === 'site') {
        // Queue site-wide import
        await api.post('/content/import-domain/queue', {
          brandId: activeBrand.id,
          domain: normalizeUrl(`https://${activeBrand.domain || ''}`),
          options: {
            // No limit - worker handles batching automatically (50 URLs per batch)
            skipAccessibilityCheck: true // Already checked above
          }
        })
      } else {
        // Queue single page import
        await api.post('/content/import-url/queue', {
          brandId: activeBrand.id,
          url: normalizeUrl(pageUrl),
          options: {
            skipAccessibilityCheck: true // Already checked above
          }
        })
      }

      // Close modal immediately
      setShowImportModal(false)
      setPageUrl('')
      setUrlDomainError('')

      // Show success toast
      toast.success('Content import started! Processing in background...', {
        position: 'top-right',
        autoClose: 3000,
      })

      // Start polling using new hook
      importProgress.startPolling(`Importing content from ${activeBrand.companyName || 'your site'}...`)

    } catch (error: any) {
      console.error('Import failed:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error'
      toast.error(`Import failed: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    } finally {
      setImportLoading(false)
    }
  }

  const handleQueueEnrichment = async () => {
    try {
      const response = await api.post('/content/enrich/queue', {
        brandId: activeBrand?.id,
        limit: 100
      })

      // Close modal immediately
      setShowEnrichModal(false)

      toast.success(`Queued ${response.data.queued} content items for enrichment`, {
        position: 'top-right',
        autoClose: 3000,
      })

      // Start polling using new hook
      enrichmentProgress.startPolling('Enriching content with AI...')
    } catch (error: any) {
      console.error('Failed to queue enrichment:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error'
      toast.error(`Failed to queue enrichment: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    }
  }

  const handleEnrichSingle = async (contentId: string) => {
    try {
      // Start the enrichment
      await api.post(`/content/${contentId}/enrich`)

      toast.success('Enrichment started!', {
        position: 'top-right',
        autoClose: 2000,
      })

      // Start polling using new hook
      enrichmentProgress.startPolling('Enriching content with AI...')
    } catch (error: any) {
      console.error('Enrichment failed:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error'
      toast.error(`Enrichment failed: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enriched':
        return 'bg-green-100 text-green-700'
      case 'extracted':
        return 'bg-blue-100 text-blue-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'failed':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PRODUCT':
        return 'bg-purple-100 text-purple-700'
      case 'ARTICLE':
        return 'bg-indigo-100 text-indigo-700'
      case 'DOCUMENTATION':
        return 'bg-cyan-100 text-cyan-700'
      case 'SERVICE':
        return 'bg-orange-100 text-orange-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getContentTitle = (content: ContentItem): string => {
    const titleField = content.fields?.find(field => field.type === 'TITLE')
    if (titleField && titleField.value) {
      // Handle JSON object with text property
      if (typeof titleField.value === 'object' && titleField.value !== null) {
        if ('text' in titleField.value) {
          return String(titleField.value.text)
        }
        // If it's an object but doesn't have text property, try to stringify
        return JSON.stringify(titleField.value)
      }
      return String(titleField.value)
    }
    return content.sourceId
  }

  const getFieldValue = (content: ContentItem, fieldType: string): string => {
    const field = content.fields?.find(f => f.type === fieldType)
    if (!field || !field.value) return ''

    // Handle string values
    if (typeof field.value === 'string') {
      return field.value
    }

    // Handle array values
    if (Array.isArray(field.value)) {
      const items = field.value.map(item => String(item))
      // Add # prefix for BENEFITS, FEATURES, and KEY_POINTS
      if (fieldType === 'BENEFITS' || fieldType === 'FEATURES' || fieldType === 'KEY_POINTS') {
        return items.map(item => `# ${item}`).join('\n')
      }
      return items.join('\n')
    }

    // Handle JSON object with text property (common for TITLE, DESCRIPTION)
    if (typeof field.value === 'object' && field.value !== null && 'text' in field.value) {
      return String(field.value.text)
    }

    return String(field.value)
  }

  const hasEnrichedMetadata = (content: ContentItem): boolean => {
    const summary = getFieldValue(content, 'SUMMARY')
    const benefits = getFieldValue(content, 'BENEFITS')
    const keyPoints = getFieldValue(content, 'KEY_POINTS') || getFieldValue(content, 'FEATURES') // Check KEY_POINTS first, fallback to FEATURES
    const keywords = getFieldValue(content, 'KEYWORDS')
    return !!(summary || benefits || keyPoints || keywords)
  }

  const hasManualEdits = (content: ContentItem): boolean => {
    return content.fields?.some(field => field.source === 'MANUAL') || false
  }

  const getFieldSource = (content: ContentItem, fieldType: string): string | null => {
    const field = content.fields?.find(f => f.type === fieldType)
    return field?.source || null
  }

  const getSourceBadge = (source: string | null) => {
    if (!source) return null

    if (source === 'MANUAL') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
          Manual
        </span>
      )
    }

    if (source === 'ENRICHED') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded">
          AI Enriched
        </span>
      )
    }

    // AUTOMATIC source - don't show badge
    return null
  }

  const handleAddKeywordTag = () => {
    const trimmedInput = keywordInput.trim()
    if (trimmedInput && !keywordTags.includes(trimmedInput)) {
      setKeywordTags([...keywordTags, trimmedInput])
      setKeywordInput('')
    }
  }

  const handleRemoveKeywordTag = (tagToRemove: string) => {
    setKeywordTags(keywordTags.filter(tag => tag !== tagToRemove))
  }

  const handleKeywordInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddKeywordTag()
    }
  }

  const handleContentModalOpen = (content: ContentItem) => {
    setSelectedContent(content)
    setEditedTitle(getFieldValue(content, 'TITLE'))
    setEditedDescription(getFieldValue(content, 'DESCRIPTION'))
    setEditedSummary(getFieldValue(content, 'SUMMARY'))
    setEditedBenefits(getFieldValue(content, 'BENEFITS'))
    setEditedKeyPoints(getFieldValue(content, 'KEY_POINTS') || getFieldValue(content, 'FEATURES')) // Check KEY_POINTS first, fallback to FEATURES

    // Extract keywords
    const field = content.fields?.find(f => f.type === 'KEYWORDS')
    let tags: string[] = []

    if (field && field.value) {
      if (Array.isArray(field.value)) {
        tags = field.value.map(k => String(k).trim()).filter(k => k.length > 0)
      } else if (typeof field.value === 'string') {
        tags = field.value.split(',').map(k => k.trim()).filter(k => k.length > 0)
      }
    }

    setKeywordTags(tags)
    setKeywordInput('')
    const importanceScoreValue = getFieldValue(content, 'IMPORTANCE_SCORE')
    const parsedScore = parseFloat(importanceScoreValue)
    setEditedImportanceScore(isNaN(parsedScore) ? 5 : parsedScore)
  }

  const handleSaveContentChanges = async () => {
    if (!selectedContent) return

    try {
      setIsSavingContent(true)

      // Helper function to strip # prefix from each line
      const stripHashPrefix = (text: string): string => {
        return text
          .split('\n')
          .map(line => line.replace(/^#\s*/, ''))
          .join('\n')
      }

      // Get original values to compare
      const originalTitle = getFieldValue(selectedContent, 'TITLE')
      const originalDescription = getFieldValue(selectedContent, 'DESCRIPTION')
      const originalSummary = getFieldValue(selectedContent, 'SUMMARY')
      const originalBenefits = stripHashPrefix(getFieldValue(selectedContent, 'BENEFITS'))
      const originalKeyPoints = stripHashPrefix(getFieldValue(selectedContent, 'KEY_POINTS') || getFieldValue(selectedContent, 'FEATURES'))

      // Get original keywords as array for proper comparison
      const field = selectedContent.fields?.find(f => f.type === 'KEYWORDS')
      let originalKeywordTags: string[] = []
      if (field && field.value) {
        if (Array.isArray(field.value)) {
          originalKeywordTags = field.value.map(k => String(k).trim()).filter(k => k.length > 0)
        } else if (typeof field.value === 'string') {
          originalKeywordTags = field.value.split(',').map(k => k.trim()).filter(k => k.length > 0)
        }
      }

      const originalImportanceScore = getFieldValue(selectedContent, 'IMPORTANCE_SCORE')

      // Only include fields that have actually changed
      const fields: Record<string, string | number> = {}

      if (editedTitle !== originalTitle) {
        fields.TITLE = editedTitle
      }
      if (editedDescription !== originalDescription) {
        fields.DESCRIPTION = editedDescription
      }
      if (editedSummary !== originalSummary) {
        fields.SUMMARY = editedSummary
      }
      if (stripHashPrefix(editedBenefits) !== originalBenefits) {
        fields.BENEFITS = stripHashPrefix(editedBenefits)
      }
      if (stripHashPrefix(editedKeyPoints) !== originalKeyPoints) {
        fields.KEY_POINTS = stripHashPrefix(editedKeyPoints)
      }

      // Compare keyword arrays directly (sort for consistent comparison)
      const keywordsChanged = JSON.stringify(keywordTags.sort()) !== JSON.stringify(originalKeywordTags.sort())
      if (keywordsChanged) {
        fields.KEYWORDS = keywordTags.join(', ')
      }

      const importanceScoreString = editedImportanceScore.toString()
      if (importanceScoreString !== originalImportanceScore) {
        fields.IMPORTANCE_SCORE = editedImportanceScore
      }

      // Only send update if there are changes
      if (Object.keys(fields).length === 0) {
        toast.info('No changes to save', {
          position: 'top-right',
          autoClose: 2000,
        })
        setSelectedContent(null)
        return
      }

      await api.put(`/content/${selectedContent.id}/fields`, { fields })

      toast.success('Content updated successfully!', {
        position: 'top-right',
        autoClose: 3000,
      })

      // Refresh data to show updated content
      await fetchData()
      setSelectedContent(null)
    } catch (error: any) {
      console.error('Failed to save content changes:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error'
      toast.error(`Failed to save changes: ${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    } finally {
      setIsSavingContent(false)
    }
  }

  const handleDeleteAIContent = async () => {
    if (!activeBrand?.id) return

    if (!window.confirm('Are you sure you want to delete ALL AI-generated content? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingAIContent(true)
      const response = await api.delete(`/content/ai-generated?brandId=${activeBrand.id}`)

      toast.success(response.data.message, {
        position: 'top-right',
        autoClose: 3000,
      })

      // Refresh data
      await fetchData()
    } catch (error: any) {
      console.error('Failed to delete AI content:', error)
      const errorMessage = error.response?.data?.message || 'Failed to delete AI content'
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
      })
    } finally {
      setDeletingAIContent(false)
    }
  }

  const aiContentCount = contents.filter(c => c.source === 'AI_GENERATED').length

  const filteredContents = contents.filter(content => {
    const matchesSearch = !searchTerm ||
      content.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.sourceId.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesType = !filterType || content.type === filterType
    const matchesStatus = !filterStatus || content.enrichmentStatus === filterStatus

    // Show all records regardless of status (pending records were previously hidden)
    const hidePending = false

    return matchesSearch && matchesType && matchesStatus && !hidePending
  })

  // Pagination calculations
  const totalPages = Math.ceil(filteredContents.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const paginatedContents = filteredContents.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterType, filterStatus])

  // Demo mode placeholder
  if (isDemoMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">Content Management</h1>
          <p className="mt-1 lg:mt-2 text-sm lg:text-base text-gray-600">
            Import, organize, and enrich your content for AI optimization
          </p>
        </div>

        {/* Demo Placeholder */}
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-lg shadow-xl p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <FileText className="h-12 w-12 text-indigo-600" />
              </div>
            </div>

            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Intelligent Content Management
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Import your website content and enrich it with AI-generated summaries, keywords, and metadata.
              Help AI assistants understand and recommend your products and services.
            </p>

            {/* Feature Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Upload className="h-8 w-8 text-indigo-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Auto Import</h3>
                <p className="text-sm text-gray-600">
                  Automatically discover and import content from your website via sitemap or individual pages
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Sparkles className="h-8 w-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">AI Enrichment</h3>
                <p className="text-sm text-gray-600">
                  Generate summaries, benefits, key points, and keywords using Claude AI
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Brain className="h-8 w-8 text-pink-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Smart Organization</h3>
                <p className="text-sm text-gray-600">
                  Organize content by type, track enrichment status, and manually edit AI-generated metadata
                </p>
              </div>
            </div>

            <a
              href="/register"
              className="inline-flex items-center px-8 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              Sign Up to Manage Your Content
              <span className="ml-2">→</span>
            </a>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Content Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <Globe className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Site-Wide Import</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Import entire websites or individual pages. Automatically detect product schemas, articles, and structured data.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Sparkles className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Batch Enrichment</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enrich multiple content items at once with AI-generated metadata. Track progress with real-time status updates.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Edit className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Manual Editing</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Edit AI-generated content to add your unique voice. Track manual edits separately from automated content.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <TrendingUp className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Importance Scoring</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Rate content importance (0-10) to help AI understand which products and services to prioritize.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">Content Management</h1>
          <p className="mt-1 lg:mt-2 text-sm lg:text-base text-gray-600">
            Import, organize, and enrich your content for AI optimization
          </p>
        </div>
        <div className="flex space-x-2 lg:space-x-3">
          <div className="relative group">
            <button
                onClick={() => {
                  fetchEnrichmentCost()
                  setShowEnrichModal(true)
                }}
                disabled={isDemoMode}
                className={`flex items-center px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm rounded-lg transition-all ${
                  isDemoMode
                    ? 'border border-gray-300 text-gray-400 cursor-not-allowed opacity-60'
                    : 'border border-indigo-600 text-indigo-600 hover:bg-indigo-50'
                }`}
            >
              <Sparkles className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Enrich Content</span>
              <span className="sm:hidden">Enrich</span>
            </button>
            {isDemoMode && (
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Available after sign up
                <div className="absolute top-full right-4 transform -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>
          <div className="relative group">
            <button
                onClick={() => setShowImportModal(true)}
                disabled={isDemoMode}
                className={`flex items-center px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm rounded-lg transition-all ${
                  isDemoMode
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
            >
              <Upload className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Import Content</span>
              <span className="sm:hidden">Import</span>
            </button>
            {isDemoMode && (
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Available after sign up
                <div className="absolute top-full right-4 transform -mt-1 border-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enrichment Progress Banner */}
      {enrichmentProgress.state.status === 'active' && (
        <WorkerProgressBanner
          type="enrichment"
          message={enrichmentProgress.state.message}
          progress={enrichmentProgress.state.progress}
          onDismiss={enrichmentProgress.dismiss}
        />
      )}

      {/* Legacy AI Content Warning Banner - for old fake products */}
      {aiContentCount > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900">Legacy AI-Generated Content Detected</h3>
                <p className="text-sm text-amber-800 mt-1">
                  You have {aiContentCount} old AI-generated content item{aiContentCount > 1 ? 's' : ''} from a previous version.
                  These are hypothetical products and should be deleted.
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  We now use brand research instead of generating fake products.
                </p>
              </div>
            </div>
            <button
              onClick={handleDeleteAIContent}
              disabled={deletingAIContent}
              className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center space-x-2 whitespace-nowrap"
            >
              {deletingAIContent ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  <span>Delete Legacy Content</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Web Auth Configuration Prompt */}
      {accessibilityStatus && !accessibilityStatus.isAccessible && accessibilityStatus.detectedPlatform && !accessibilityStatus.webAuthConfigured && activeBrand && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">
                  {accessibilityStatus.detectedPlatform.charAt(0).toUpperCase() + accessibilityStatus.detectedPlatform.slice(1)} Store Detected - Authentication Required
                </h3>
                <p className="text-sm text-amber-800 mt-1">
                  Your {accessibilityStatus.detectedPlatform} store is protected by {accessibilityStatus.blockType || 'bot protection'}.
                  Configure authentication credentials to allow BrandIndex AI to crawl your site and import content.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowWebAuthModal(true)}
              className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              Configure Auth
            </button>
          </div>
        </div>
      )}

      {/* Brand Research Info Banner - only show if no crawler auth configured */}
      {accessibilityStatus && !accessibilityStatus.isAccessible && !accessibilityStatus.webAuthConfigured && activeBrand && (
        <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Brain className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">Brand Research Available</h3>
              <p className="text-sm text-blue-800 mt-1">
                Your site blocks automated content access, but we've researched factual information about {activeBrand.companyName} using AI.
                This research helps our analysis understand your brand, products, and target audience.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 lg:gap-4">
          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm font-medium text-gray-600">Total Content</p>
                <div className="flex items-center gap-2 mt-1 lg:mt-2">
                  <p className="text-xl lg:text-3xl font-bold text-gray-900">{stats.total}</p>
                  {importProgress.state.status === 'active' && (
                    <RefreshCw className="h-4 w-4 lg:h-5 lg:w-5 text-indigo-500 animate-spin" />
                  )}
                </div>
                <p className="text-[10px] lg:text-sm text-gray-500 mt-0.5 lg:mt-1">{stats.recentlyAdded} added this week</p>
              </div>
              <FileText className="h-5 w-5 lg:h-8 lg:w-8 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm font-medium text-gray-600">Enriched</p>
                <div className="flex items-center gap-2 mt-1 lg:mt-2">
                  <p className="text-xl lg:text-3xl font-bold text-green-600">{stats.byStatus.enriched || 0}</p>
                  {enrichmentProgress.state.status === 'active' && (
                    <Sparkles className="h-4 w-4 lg:h-5 lg:w-5 text-purple-500 animate-pulse" />
                  )}
                </div>
                <p className="text-[10px] lg:text-sm text-gray-500 mt-0.5 lg:mt-1">
                  {stats.total > 0 ? Math.round(((stats.byStatus.enriched || 0) / stats.total) * 100) : 0}% complete
                </p>
              </div>
              <Sparkles className="h-5 w-5 lg:h-8 lg:w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm font-medium text-gray-600">Content Types</p>
                <p className="text-xl lg:text-3xl font-bold text-gray-900 mt-1 lg:mt-2">{Object.keys(stats.byType).length}</p>
                <p className="text-[10px] lg:text-sm text-gray-500 mt-0.5 lg:mt-1 truncate text-wrap">
                  {Object.entries(stats.byType).map(([type, count]) => `${count} ${type.toLowerCase()}`).join(', ')}
                </p>
              </div>
              <TrendingUp className="h-5 w-5 lg:h-8 lg:w-8 text-indigo-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-2 lg:p-4">
        <div className="flex flex-wrap items-center gap-2 lg:gap-4">
          <div className="flex-1 min-w-[150px] lg:min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-2 lg:left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by URL or source ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 lg:pl-10 pr-2 lg:pr-4 py-1.5 lg:py-2 text-xs lg:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="PRODUCT">Product</option>
            <option value="ARTICLE">Article</option>
            <option value="PAGE">Page</option>
            <option value="SERVICE">Service</option>
            <option value="DOCUMENTATION">Documentation</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="extracted">Extracted</option>
            <option value="enriched">Enriched</option>
            <option value="failed">Failed</option>
          </select>

          <button
            onClick={fetchData}
            className="flex items-center px-2 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-3 w-3 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 lg:px-6 py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto max-w-md">
                Content
              </th>
              <th className="px-2 lg:px-6 py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Type
              </th>
              <th className="px-2 lg:px-6 py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Status
              </th>
              <th className="px-2 lg:px-6 py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Date Added
              </th>
              <th className="px-2 lg:px-6 py-2 lg:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredContents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  {importProgress.state.status === 'active' ? (
                    // Show import running UI
                    <div className="max-w-md mx-auto">
                      <RefreshCw className="h-12 w-12 text-indigo-500 mx-auto mb-4 animate-spin" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Importing Content...
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {importProgress.state.message || 'Discovering and extracting content from your site.'}
                      </p>
                      {importProgress.state.progress && (
                        <div className="mt-4">
                          <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{importProgress.state.progress.processed} / {importProgress.state.progress.total}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${importProgress.state.progress.percent}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : accessibilityStatus && !accessibilityStatus.isAccessible && !accessibilityStatus.webAuthConfigured ? (
                    // Show bot protection warning only if NO crawler auth configured
                    <div className="max-w-2xl mx-auto">
                      <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {accessibilityStatus.blockType || 'Content Import Blocked'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {accessibilityStatus.notes || 'This site blocks automated content access.'}
                      </p>
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-3">
                          <Sparkles className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-indigo-900 mb-2">
                              AI-Generated Content Available
                            </p>
                            <p className="text-sm text-indigo-800 mb-3">
                              Since scraping isn't possible, we can generate brand overview and product content using AI based on general knowledge about {activeBrand?.companyName}.
                            </p>
                            <button
                              onClick={async () => {
                                try {
                                  toast.info('Generating AI content...', { position: 'top-right', autoClose: 2000 })
                                  const response = await api.post(`/ai-content/generate/${activeBrand?.id}`, {
                                    productCount: 5
                                  })
                                  toast.success(response.data.message, { position: 'top-right', autoClose: 5000 })
                                  await fetchData()
                                } catch (error: any) {
                                  const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
                                  toast.error(`Failed to generate content: ${errorMessage}`, {
                                    position: 'top-right',
                                    autoClose: 5000,
                                  })
                                }
                              }}
                              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Generate AI Content
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-4">
                        <p className="text-sm font-medium text-amber-900 mb-2">Other Options:</p>
                        <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                          <li>Check if the brand offers an official API for content access</li>
                          <li>Manually enter key content information</li>
                          <li>Contact the brand about data partnership opportunities</li>
                        </ul>
                      </div>
                      {accessibilityStatus.confidence && (
                        <p className="text-xs text-gray-500">
                          Confidence: {Math.round(accessibilityStatus.confidence * 100)}%
                          {accessibilityStatus.lastChecked && ` • Last checked: ${new Date(accessibilityStatus.lastChecked).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  ) : (
                    // Show normal "no content" message
                    <>
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 mb-3">No content found</p>
                      <div className="relative group inline-block">
                        <button
                          onClick={() => setShowImportModal(true)}
                          disabled={isDemoMode}
                          className={`inline-flex items-center text-sm font-medium transition-all ${
                            isDemoMode
                              ? 'text-gray-400 cursor-not-allowed opacity-60'
                              : 'text-indigo-600 hover:text-indigo-500'
                          }`}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Import your first content
                        </button>
                        {isDemoMode && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            Available after sign up
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              paginatedContents.map((content) => (
                <tr key={content.id} className="hover:bg-gray-50">
                  <td className="px-3 lg:px-6 py-2 lg:py-4 max-w-xs">
                    <div className="overflow-hidden">
                      <div className="flex items-center space-x-2">
                        <div className="text-xs lg:text-sm font-medium text-gray-900 truncate">
                          {getContentTitle(content)}
                        </div>
                        {content.source === 'AI_GENERATED' && (
                          <span className="px-1.5 py-0.5 text-[9px] lg:text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase whitespace-nowrap">
                            AI
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] lg:text-xs text-gray-500 truncate">
                        {content.url}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 lg:px-6 py-2 lg:py-4 whitespace-nowrap">
                    <span className={`px-1.5 lg:px-2 py-0.5 lg:py-1 text-[10px] lg:text-xs font-medium rounded ${getTypeColor(content.type)}`}>
                      {content.type}
                    </span>
                  </td>
                  <td className="px-2 lg:px-6 py-2 lg:py-4 whitespace-nowrap">
                    <span className={`px-1.5 lg:px-2 py-0.5 lg:py-1 text-[10px] lg:text-xs font-medium rounded ${getStatusColor(content.enrichmentStatus)}`}>
                      {content.enrichmentStatus}
                    </span>
                  </td>
                  <td className="px-2 lg:px-6 py-2 lg:py-4 text-xs lg:text-sm text-gray-500 whitespace-nowrap">
                    {new Date(content.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-2 lg:px-6 py-2 lg:py-4 text-right text-sm font-medium whitespace-nowrap">
                    <div className="flex items-center justify-end space-x-2">
                      {content.enrichmentStatus === 'pending' && (
                        <button
                          onClick={() => handleEnrichSingle(content.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Enrich now"
                        >
                          <Sparkles className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleContentModalOpen(content)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination Controls */}
        {filteredContents.length > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredContents.length)} of {filteredContents.length} items
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Brand Questions Card */}
      {activeBrand && !isDemoMode && (
        <BrandQuestionsCard brandId={activeBrand.id} />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Import Content</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Import Method
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setImportMethod('site')
                      setUrlDomainError('')
                    }}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors border-2 ${
                      importMethod === 'site'
                        ? 'bg-indigo-50 border-indigo-600 text-indigo-700'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Globe className="h-5 w-5 mx-auto mb-1" />
                    <span className="block">Entire Site</span>
                    <span className="text-xs opacity-75">From brand website</span>
                  </button>
                  <button
                    onClick={() => {
                      setImportMethod('page')
                      setUrlDomainError('')
                    }}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors border-2 ${
                      importMethod === 'page'
                        ? 'bg-indigo-50 border-indigo-600 text-indigo-700'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="h-5 w-5 mx-auto mb-1" />
                    <span className="block">Single Page</span>
                    <span className="text-xs opacity-75">Specific URL</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {importMethod === 'site' ? 'Website' : 'Page URL'}
                </label>
                <input
                  type="text"
                  value={importMethod === 'site' ? (activeBrand?.domain ? `https://${activeBrand.domain}` : '') : pageUrl}
                  onChange={(e) => importMethod === 'page' && handlePageUrlChange(e.target.value)}
                  disabled={importMethod === 'site'}
                  placeholder={importMethod === 'site' ? 'Brand domain' : 'https://example.com/page'}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    importMethod === 'site'
                      ? 'bg-gray-50 text-gray-600 cursor-not-allowed border-gray-300'
                      : urlDomainError
                        ? 'border-red-300'
                        : 'border-gray-300'
                  }`}
                />
                {importMethod === 'page' && urlDomainError && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {urlDomainError}
                  </p>
                )}
                {!urlDomainError && (
                  <p className="mt-1 text-xs text-gray-500">
                    {importMethod === 'site'
                      ? 'Will discover and import from sitemap automatically (up to 100 pages)'
                      : 'Enter the specific page URL you want to import'
                    }
                  </p>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-3 flex items-start space-x-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  Import will discover URLs, extract structured data (JSON-LD), and create content records.
                  Enrichment can be run separately to add AI-generated summaries and keywords.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowImportModal(false)
                  setPageUrl('')
                  setUrlDomainError('')
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={importLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importLoading || (importMethod === 'page' && (!pageUrl.trim() || Boolean(urlDomainError))) || (importMethod === 'site' && !activeBrand?.domain)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {importLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enrichment Modal */}
      {showEnrichModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Enrich Content with AI</h2>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                AI enrichment will generate summaries, keywords, benefits, and features for your content using Claude.
              </p>

              {enrichmentCost && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Content items pending:</span>
                    <span className="font-medium text-gray-900">{stats?.byStatus.pending || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Estimated tokens:</span>
                    <span className="font-medium text-gray-900">{enrichmentCost.totalTokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-gray-600 flex items-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Estimated cost:
                    </span>
                    <span className="font-bold text-gray-900">${enrichmentCost.estimatedCost.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-700">
                  Enrichment will be queued and processed in the background. This may take several minutes depending on the number of items.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEnrichModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleQueueEnrichment}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Queue Enrichment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Details Modal */}
      {selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Edit Content</h2>
              <button
                onClick={() => setSelectedContent(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter content title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter content description"
                />
              </div>

              {/* URL (disabled with link icon) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedContent.url}
                    disabled
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                  <a
                    href={selectedContent.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-600 hover:text-indigo-800"
                    title="Open in new window"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Type and Status */}
              <div className="flex gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded ${getTypeColor(selectedContent.type)}`}>
                    {selectedContent.type}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded ${getStatusColor(selectedContent.enrichmentStatus)}`}>
                    {selectedContent.enrichmentStatus}
                  </span>
                </div>
              </div>

              {/* claude */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Enriched AI Contextual Metadata</h3>
                    {hasManualEdits(selectedContent) && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                        <Edit className="h-3 w-3 mr-1" />
                        Manually Edited
                      </span>
                    )}
                  </div>
                  {hasEnrichedMetadata(selectedContent) && (
                    <button
                      onClick={() => {
                        handleEnrichSingle(selectedContent.id)
                        setSelectedContent(null)
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      title="Generate new AI-enriched metadata"
                    >
                      <Sparkles className="h-3 w-3 mr-1.5" />
                      Enrich with AI
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-4">This content helps AI tools understand and recommend your services & products. It's not visible to customers—use it to match the keywords and phrases shoppers actually search for to ensure contextual relevancy.</p>

                {/* Auto-enrichment Banner */}
                {!hasEnrichedMetadata(selectedContent) && (
                  <div className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="h-4 w-4 text-indigo-600" />
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Auto enrichment available:</span> Generate AI-powered metadata
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        handleEnrichSingle(selectedContent.id)
                        setSelectedContent(null)
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      <Sparkles className="h-3 w-3 mr-1.5" />
                      Enrich Now
                    </button>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Summary */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-sm font-medium text-gray-700">
                        Summary
                      </label>
                      {getSourceBadge(getFieldSource(selectedContent, 'SUMMARY'))}
                    </div>
                    <textarea
                      value={editedSummary}
                      onChange={(e) => setEditedSummary(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Overall value poroposition summary of the positioning and contexutally releveance of the this page. "
                    />
                  </div>

                  {/* Benefits */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-sm font-medium text-gray-700">
                        Benefits
                      </label>
                      {getSourceBadge(getFieldSource(selectedContent, 'BENEFITS'))}
                    </div>
                    <textarea
                      value={editedBenefits}
                      onChange={(e) => setEditedBenefits(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="3-5 Key benefits or takeaways. Enter one per row."
                    />
                  </div>

                  {/* Key Points */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-sm font-medium text-gray-700">
                        Key Points
                      </label>
                      {getSourceBadge(getFieldSource(selectedContent, 'KEY_POINTS') || getFieldSource(selectedContent, 'FEATURES'))}
                    </div>
                    <textarea
                      value={editedKeyPoints}
                      onChange={(e) => setEditedKeyPoints(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="3-5 key value proposition points - what makes this compelling. Enter one per row."
                    />
                  </div>

                  {/* Keywords */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">
                          Keywords
                        </label>
                        {getSourceBadge(getFieldSource(selectedContent, 'KEYWORDS'))}
                      </div>
                      <span className="text-xs text-gray-500">
                        {keywordTags.length} tags
                      </span>
                    </div>

                    {/* Tags Display */}
                    <div className="w-full p-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                    {keywordTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-1 mb-1">
                        {keywordTags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 gap-2 py-1.5 text-xs rounded-full font-medium bg-indigo-100 text-indigo-800"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveKeywordTag(tag)}
                              className="inline-flex items-center justify-center w-3 h-3 hover:bg-indigo-200 focus:outline-none"
                            >
                              <span className="text-indigo-600 text-xs font-bold">×</span>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Single Line Input */}
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordInputKeyDown}
                      className="w-full px-2 py-1 text-sm border-0 focus:border-0 active:border-0"
                      placeholder="Type a keyword and press Enter"
                    />
                    </div>
                    <p className="px-2 pb-2 mt-1 text-xs text-gray-500">
                      Add relevant keywords and phrases that customers use when searching. Press Enter to add each tag.
                    </p>
                  </div>

                  {/* Importance Score */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Importance Score
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.5"
                        value={editedImportanceScore}
                        onChange={(e) => setEditedImportanceScore(parseFloat(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                      <div className="flex items-center justify-center w-12 h-10 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <span className="text-sm font-bold text-indigo-700">
                          {editedImportanceScore.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Rate the importance of this content (0 = low, 10 = high priority)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t bg-gray-50">
              <p className="text-xs text-gray-500">
                Editing will create new field records with 'MANUAL' source
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedContent(null)}
                  disabled={isSavingContent}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveContentChanges}
                  disabled={isSavingContent}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {isSavingContent ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Web Authentication Modal */}
      {activeBrand && (
        <WebAuthModal
          isOpen={showWebAuthModal}
          onClose={() => setShowWebAuthModal(false)}
          brandId={activeBrand.id}
          brandName={activeBrand.companyName}
          platform={accessibilityStatus?.detectedPlatform}
          domain={`https://${activeBrand.domain}`}
          onSuccess={() => {
            fetchData() // Refresh data to update auth status
          }}
        />
      )}
    </div>
  )
}
